import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState } from "@/lib/app-state";
import { cloneSession, isValidSession, queryKeys } from "@bso/shared";
import { PageTransition } from "@/components/ui/page-transition";

/**
 * Bible Strong auth wrapper.
 *
 * NOTE (#13 — de-clone): Bible Strong deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass `AuthCard` shell every clone
 * inherited and which therefore read as a clone). Instead each screen owns its
 * own `AuthPage` — a single bound "reading page" surface where the serif
 * masthead and the form live together on one sheet. So this route is a thin
 * wrapper: it only owns the "already authenticated → redirect" gate and the page
 * transition. The background, masthead and scroll container live in `AuthPage`,
 * rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    // The redirect is thrown AFTER the try/catch so TanStack Router receives it.
    // No initial value: TypeScript narrows `result` after the catch-that-returns.
    // oxlint-disable-next-line eslint(init-declarations)
    let result;
    try {
      result = await api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    } catch {
      // Network error — let user try to log in anyway.
      return;
    }

    const { data, error } = result;

    // Synchronize the machine and the cache immediately.
    // This prevents the BootManager from seeing a "pending" or stale session
    // during the route transition, which causes redirection loops.
    sessionActor.send({
      type: "SERVER_REPLY",
      session: isValidSession(data) ? cloneSession(data) : null,
      error: error ?? null,
    });

    queryClient.setQueryData(queryKeys.auth.session(), data ?? null);

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
